/**
 * Core Authentication Service
 * 
 * Handles user authentication operations including login, registration (individual
 * and business), password management (reset, change), email verification, and
 * verification level retrieval. Manages API communication for all authentication flows.
 */

import { apiRequest } from '@/shared/services/apiClient.js';
import { profileService } from '@/features/settings/services/profileService.js';
import { cleanNumberForBackend } from '@/shared/utils/format-utils.js';
import { tokenService } from './tokenService.js';
import logger from '@/shared/utils/logger.js';

export const authService = {
  normalizeBusinessId(value) {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    const lowered = str.toLowerCase();
    if (lowered === 'undefined' || lowered === 'null' || lowered === 'nan') return null;
    return str;
  },

  /**
   * Authenticates a user with email and password credentials.
   * Validates the API response and throws appropriate errors for invalid credentials.
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<Object>} Authentication response with user data and tokens
   * @throws {Error} If authentication fails or server returns no response
   */
  async login(email, password) {
    const data = await apiRequest('/publicauth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        password: password,
      }),
    });

    if (!data) {
      throw new Error('No response received from server');
    }

    if (data.code === '00' && data.data) {
      //store "preAuthToken":  in localstorage.
      //sessionStroage.setItem("preAuthToken", data.preAuthToken)
      if(data.data.require2FA || data.data.require2FA == 'true'){
        sessionStorage.setItem("preAuthToken", data.data.preAuthToken);
      }
      
      return data;
    }

    const errorMessage = data?.message || 'Login failed - invalid credentials';
    throw new Error(errorMessage);
  },

  /**
   * Registers a new individual user account with personal information.
   * Maps form field names to API-expected field names and creates the account.
   * @param {Object} userData - User registration data including name, email, phone, country, password
   * @returns {Promise<Object>} Registration response from the API
   */
  async register(userData) {
    const phoneNumber = userData.phone || userData.phone_number;
    const cleanedPhone = phoneNumber ? cleanNumberForBackend(phoneNumber) : phoneNumber;

    const data = await apiRequest('/publicauth/register', {
      method: 'POST',
      body: JSON.stringify({
        first_name: userData.firstName || userData.first_name,
        middle_name: userData.middleName || userData.middle_name,
        last_name: userData.surname || userData.last_name,
        email: userData.email,
        phone_number: cleanedPhone,
        country: userData.country,
        password: userData.password,
      }),
    });

    return data;
  },

  /**
   * Registers a new business account with business information.
   * Creates a business entity and associated business contact user.
   * @param {Object} businessData - Business registration data including business details and owner information
   * @returns {Promise<Object>} Registration response from the API
   */
  async registerBusiness(businessData) {
    const data = await apiRequest('/publicauth/register-business', {
      method: 'POST',
      body: JSON.stringify(businessData),
    });

    return data;
  },

  /**
   * Initiates a password reset request by sending a reset link to the user's email.
   * @param {string} email - User's email address for password reset
   * @returns {Promise<Object>} API response confirming the reset request
   */
  async requestPasswordReset(email) {
    const data = await apiRequest('/publicauth/password/request-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    return data;
  },

  /**
   * Requests a password reset OTP to be sent to the user's email or phone.
   * Stores the returned userId in sessionStorage for the confirmation step.
   * @param {string} email - User's email address
   * @returns {Promise<Object>} API response containing userId for OTP confirmation
   */
  async requestPasswordResetOTP(email) {
    const data = await apiRequest('/api/publicauth/password/request-reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const userId = data?.data?.userId || data?.data?.user_id || null;
    if (userId) {
      sessionStorage.setItem('passwordResetUserId', userId);
    }

    return data;
  },

  /**
   * Confirms the password reset OTP code to verify the user's identity.
   * Retrieves userId from sessionStorage if not provided, and clears it after successful confirmation.
   * @param {string} otp - The OTP code received by the user
   * @param {string|null} userId - Optional user ID (retrieved from sessionStorage if not provided)
   * @returns {Promise<Object>} API response confirming OTP validation
   * @throws {Error} If userId is not found or OTP is invalid
   */
  async confirmPasswordResetOTP(otp, userId = null) {
    const targetUserId = userId || sessionStorage.getItem('passwordResetUserId');

    if (!targetUserId) {
      throw new Error('User ID not found. Please request a new OTP.');
    }

    const data = await apiRequest('/api/publicauth/password/confirm-otp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        otp: otp.toString(),
        userId: targetUserId
      }),
    });

    if (data && data.code === '00') {
      sessionStorage.removeItem('passwordResetUserId');
    }

    if (data && data.code === '00') {
  const resetToken = data?.data?.resetToken;
  if (resetToken) {
    sessionStorage.setItem('passwordResetToken', resetToken);
  }
}

    return data;
  },

  /**
   * Resets the user's password using the OTP token and new password.
   * @param {string} email - User's email address
   * @param {string} otp - The OTP token received via email
   * @param {string} newPassword - The new password to set
   * @returns {Promise<Object>} API response confirming password reset
   */
  async resetPasswordWithOTP(email, otp, newPassword) {
    const resetData = await apiRequest('/publicauth/password/reset', {
      method: 'POST',
      body: JSON.stringify({
        email: email,
        token: otp,
        newPassword: newPassword
      }),
    });

    return resetData;
  },

  /**
   * Changes the authenticated user's password.
   * Requires both the current password and the new password for security.
   * @param {string} oldPassword - User's current password
   * @param {string} newPassword - The new password to set
   * @returns {Promise<Object>} API response confirming password change
   */
  async changePassword(oldPassword, newPassword) {
    logger.debug('changePassword called, sending request to /auth/password/change');

    try {
      const changeData = await apiRequest('/auth/password/change', {
        method: 'POST',
        body: JSON.stringify({
          oldPassword,
          newPassword,
        }),
      });

      logger.debug('Password change response', { changeData });
      return changeData;
    } catch (error) {
      logger.error('Password change error', { error });
      throw error;
    }
  },

  async getIndividualVerificationLevel() {
    const data = await apiRequest('/auth/get-verification-level', {
      method: 'GET',
      headers: {
        'X-Skip-Refresh': '1'
      }
    });

    return data;
  },

  async getBusinessVerificationLevel(businessId) {
    const normalizedBusinessId = this.normalizeBusinessId(businessId);
    if (!normalizedBusinessId) {
      throw new Error('Business ID is required to fetch business verification level');
    }

    // Endpoint: /auth/business/:businessId/get-verification-level
    // Full URL: https://gateway.ismartghana.com/auth-service/api/auth/business/:businessId/get-verification-level
    // Headers: Authorization Bearer token (automatically added by apiClient)
    const data = await apiRequest(`/auth/business/${normalizedBusinessId}/get-verification-level`, {
      method: 'GET',
    });

    return data;
  },

  async resolveBusinessId(providedBusinessId) {
    const provided = this.normalizeBusinessId(providedBusinessId);
    if (provided) return provided;

    try {
      const cachedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
      const cachedBusinessId = cachedUserData?.business?.id || cachedUserData?.businesses?.[0]?.id;
      const normalizedCached = this.normalizeBusinessId(cachedBusinessId);
      if (normalizedCached) return normalizedCached;
    } catch (error) {
      logger.warn('Failed to parse userData while resolving business ID', { error });
    }

    try {
      const profile = await profileService.getUserProfile();
      const profileData = profile?.data || profile;
      const businessId = profileData?.business?.id || profileData?.user?.business?.id;
      const normalizedProfileId = this.normalizeBusinessId(businessId);
      if (normalizedProfileId) return normalizedProfileId;
    } catch (error) {
      logger.warn('Failed to resolve business ID from profile', { error });
    }

    return null;
  },

  async getVerificationLevel(options = {}) {
    const { userType: overrideType, businessId: overrideBusinessId } = options;

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const detectedType = overrideType || userData?.usertype || userData?.userType || userData?.user_type;
      const normalizedType = (detectedType || '').toLowerCase();

      if (normalizedType === 'business_contact') {
        const businessId = await this.resolveBusinessId(overrideBusinessId);
        if (!businessId) {
          logger.warn('Business ID could not be resolved for business verification level. Falling back to individual verification endpoint.');
          return await this.getIndividualVerificationLevel();
        }

        return await this.getBusinessVerificationLevel(businessId);
      }

      return await this.getIndividualVerificationLevel();
    } catch (error) {
      const errorMessage = error?.response?.data?.message?.toLowerCase() || '';
      const statusCode = error?.response?.status;

      if (statusCode === 404 ||
        (statusCode === 500 &&
          (errorMessage.includes('not found') ||
            errorMessage.includes('verification level')))) {
        logger.debug('No verification level found, defaulting to 0');
        return { data: { verificationLevel: 0 } };
      }

      if (statusCode === 401 || statusCode === 403) {
        logger.warn('Authentication required to check verification level');
        return { data: { verificationLevel: 0 } };
      }

      logger.warn('Error fetching verification level', { error: error?.message || 'Unknown error' });
      return {
        data: {
          verificationLevel: 0,
          error: error?.message || 'Failed to fetch verification level'
        }
      };
    }
  },

  /**
   * Requests a phone verification OTP to be sent to the user's registered phone number.
   * @returns {Promise<Object>} API response containing OTP delivery confirmation
   */
  async requestVerifyPhoneOtp() {
    const userType = tokenService.getUserType();
    const endpointType = userType === 'business_contact' ? 'business_contact' : 'customer';

    logger.debug(`Requesting phone OTP for user type: ${userType}, using endpoint: ${endpointType}`);

    const data = await apiRequest(`/auth/request-verify-phone-otp/${endpointType}`, {
      method: 'GET',
    });

    return data;
  },

  /**
   * Requests an email verification OTP/link to be sent to the user's email address.
   * Used to resend verification emails when the user hasn't received one or needs a new one.
   * @returns {Promise<Object>} API response confirming the verification email was sent
   */
  async requestEmailVerification() {
    // For authenticated users, this should send an OTP email (not a token link)
    // The OTP can then be verified using /auth/verify-loggedin-email
    const data = await apiRequest('/auth/request-verify-email', {
      method: 'GET',
    });

    return data;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // BUSINESS VERIFICATION — Email & Phone
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Sends an email verification OTP to the business contact's registered email.
   *
   * Endpoint : GET /auth/request-verify-email
   * Auth     : Bearer token (added automatically by apiClient)
   * Response : { code: "00", message: "...", data: { ... } }
   *
   * Call this when the user clicks "Send Email OTP" on the verification page.
   * On success (code === "00") show the OTP input and let the user call
   * verifyBusinessEmail() with the code they received.
   *
   * @returns {Promise<Object>} API response confirming the OTP email was dispatched
   */
  async requestBusinessEmailVerification() {
    logger.debug('Requesting business email verification OTP');

    const data = await apiRequest('/auth/request-verify-email', {
      method: 'GET',
    });

    logger.debug('Business email OTP request response', { code: data?.code });
    return data;
  },

  /**
   * Verifies the business contact's email using the OTP received in their inbox.
   *
   * Endpoint : POST /auth/verify-loggedin-email
   * Auth     : Bearer token (added automatically by apiClient)
   * Body     : { "otp": "<6-digit code>" }
   * Response : { code: "00", message: "Email verified successfully", data: { ... } }
   *
   * @param {string} otp - The 6-digit OTP code from the verification email
   * @returns {Promise<{ success: boolean, message: string, code: string }>}
   */
  async verifyBusinessEmail(otp) {
    if (!otp || !otp.trim()) {
      throw new Error('OTP is required to verify email');
    }

    logger.debug('Verifying business email OTP');

    try {
      const data = await apiRequest('/auth/verify-loggedin-email', {
        method: 'POST',
        body: JSON.stringify({ otp: otp.trim() }),
      });

      const isSuccess = data?.code === '00';
      logger.debug('Business email verification response', { code: data?.code, success: isSuccess });

      return {
        success: isSuccess,
        message: data?.message || (isSuccess ? 'Email verified successfully' : 'Email verification failed'),
        code: data?.code,
        data: data?.data || null,
      };
    } catch (error) {
      // Treat code "00" in error body as success (backend cleanup edge case)
      if (error?.response?.code === '00' || error?.code === '00') {
        logger.info('Business email verified (response came through error handler)');
        return {
          success: true,
          message: error?.response?.message || 'Email verified successfully',
          code: '00',
          data: null,
        };
      }
      logger.error('Business email verification failed', { error });
      throw error;
    }
  },

  /**
   * Sends a phone verification OTP via SMS to the business contact's registered number.
   *
   * Endpoint : GET /auth/request-verify-phone-otp/business_contact
   * Auth     : Bearer token (added automatically by apiClient)
   * Response : { code: "00", message: "OTP sent", data: { ... } }
   *
   * Call this when the user clicks "Send OTP" on the phone verification row.
   * On success (code === "00") show the OTP input and let the user call
   * verifyBusinessPhone() with the code they received via SMS.
   *
   * @returns {Promise<Object>} API response confirming the SMS was dispatched
   */
  async requestBusinessPhoneVerification() {
    logger.debug('Requesting business phone verification OTP (SMS)');

    const data = await apiRequest('/auth/request-verify-phone-otp/business_contact', {
      method: 'GET',
    });

    logger.debug('Business phone OTP request response', { code: data?.code });
    return data;
  },

  /**
   * Verifies the business contact's phone number using the OTP received via SMS.
   *
   * Endpoint : POST /auth/verify-phone
   * Auth     : Bearer token (added automatically by apiClient)
   * Body     : { "otp": "<6-digit code>" }
   * Response : { code: "00", message: "Phone verified successfully", data: { ... } }
   *
   * @param {string} otp - The 6-digit OTP code received via SMS
   * @returns {Promise<{ success: boolean, message: string, code: string }>}
   */
  async verifyBusinessPhone(otp) {
    if (!otp || !otp.trim()) {
      throw new Error('OTP is required to verify phone number');
    }

    logger.debug('Verifying business phone OTP');

    try {
      const data = await apiRequest('/auth/verify-phone', {
        method: 'POST',
        body: JSON.stringify({ otp: otp.trim() }),
      });

      const isSuccess = data?.code === '00' || data?.success === true;
      logger.debug('Business phone verification response', { code: data?.code, success: isSuccess });

      return {
        success: isSuccess,
        message: data?.message || (isSuccess ? 'Phone verified successfully' : 'Phone verification failed'),
        code: data?.code,
        data: data?.data || null,
      };
    } catch (error) {
      // Treat code "00" in error body as success
      if (error?.response?.code === '00' || error?.code === '00') {
        logger.info('Business phone verified (response came through error handler)');
        return {
          success: true,
          message: error?.response?.message || 'Phone verified successfully',
          code: '00',
          data: null,
        };
      }
      logger.error('Business phone verification failed', { error });
      throw error;
    }
  },

  /**
   * Updates the authenticated user's personal information.
   * @param {Object} updateData - Personal information fields to update
   * @returns {Promise<Object>} API response confirming the update
   */
  async updatePersonalInfo(updateData) {
    const data = await apiRequest('/auth/profile/personal-info', {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });

    return data;
  },

  /**
   * Retrieves the authenticated user's personal information.
   * @returns {Promise<Object>} User's personal information data
   */
  async getPersonalInfo() {
    const data = await apiRequest('/auth/profile/personal-info', {
      method: 'GET',
    });

    return data;
  },

  /**
   * Verifies the user's phone number using the OTP code sent to their phone.
   * @param {string} otp - The OTP code received via SMS
   * @returns {Promise<Object>} API response confirming phone verification
   */
  async verifyPhone(otp) {
    const data = await apiRequest('/auth/verify-phone', {
      method: 'POST',
      body: JSON.stringify({ otp }),
    });

    return data;
  },

  /**
   * Verifies a two-factor authentication code during login flow.
   * @param {string} userId - The user's ID
   * @param {string} authCode - The 2FA code from the authenticator app
   * @returns {Promise<Object>} API response confirming 2FA verification
   */
  async verifyTwoFactorAuth(userId, authCode) {
    const preAuthToken = sessionStorage.getItem("preAuthToken");
    const data = await apiRequest('/api/publicauth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({
        code: authCode,
        customerId: userId,
        preAuthToken: preAuthToken
      }),
    });

    return data;
  },

  /**
   * Checks whether the current user's email and phone are verified or pending verification.
   * @returns {Promise<Object>} API response with verification status for email and phone
   */
  async checkEmailPhoneVerified() {
    const data = await apiRequest('/api/auth/check-email-phone-verified', {
      method: 'GET',
    });

    return data;
  },

  /**
   * Verifies the user's email address using the verification token from the email link.
   * Uses the public endpoint for email verification from email links.
   * According to Postman collection: POST /api/publicauth/verify-email with { "token": "...", "usertype": "..." }
   * Handles special cases where refresh token cleanup errors occur but email verification succeeds.
   * @param {string} token - The verification token from the email link
   * @param {string} usertype - The user type (customer or business_contact)
   * @returns {Promise<Object>} Verification result with success status and message
   */
  async verifyEmail(token, usertype) {
    try {
      // Use public endpoint for email verification from email links
      // Payload: { "token": "...", "usertype": "customer" | "business_contact" }
      // Full URL: https://gateway.ismartghana.com/auth-service/api/publicauth/verify-email
      logger.debug('Verifying email with token and usertype', { 
        tokenLength: token?.length, 
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
        usertype 
      });
      
      const payload = {
        token: token,
        usertype: usertype || 'customer' // Default to customer if not provided
      };
      
      // The token is used both in the Authorization header and in the request body
      const data = await apiRequest('/api/publicauth/verify-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      logger.debug('Email verification response', { code: data?.code, message: data?.message });

      // Explicitly handle error codes - only "00" means success
      if (data?.code !== '00') {
        const message = data?.message || '';
        
        // Handle case where email verification succeeded but backend cleanup/update failed
        // Code "07" with prisma errors means verification worked but backend update failed
        if (data?.code === '07' && (
          message.includes('prisma.refreshToken.delete') || 
          message.includes('prisma.users.update') ||
          message.includes('Argument `id` must not be null')
        )) {
          logger.warn('Email verification succeeded but backend update failed', { 
            code: data?.code, 
            message: message.substring(0, 100) 
          });
          // Still treat as success since the email was verified
          return {
            success: true,
            message: 'Email verified successfully',
            data: data?.data || null
          };
        }
        
        // All other error codes (including "07" with "Invalid token") are failures
        logger.warn('Email verification failed', { code: data?.code, message: message.substring(0, 100) });
        return {
          success: false,
          message: data?.message || 'Email verification failed',
          data: data?.data || null
        };
      }

      // Code "00" means success
      return {
        success: true,
        message: data?.message || 'Email verified successfully',
        data: data?.data || null
      };
    } catch (error) {
      logger.error('Email verification error', { 
        error: error.message, 
        status: error.status,
        errorData: error.data,
        tokenLength: token?.length 
      });
      
      // Check if error has data (from non-200 API response)
      if (error.data) {
        const errorCode = error.data?.code;
        const errorMessage = error.data?.message || '';
        
        // Handle refresh token deletion errors - these are still success
        if (error.isRefreshTokenDeleteError || 
            error.message?.includes('prisma.refreshToken.delete') ||
            (errorCode === '07' && errorMessage.includes('prisma.refreshToken.delete')) ||
            (errorCode === '07' && errorMessage.includes('prisma.users.update')) ||
            (errorCode === '07' && errorMessage.includes('Argument `id` must not be null'))) {
          logger.info('✅ Email verified successfully (refresh token cleanup error handled)');
          return {
            success: true,
            message: 'Email verified successfully',
            data: null
          };
        }
        
        // All other errors (including "Invalid token") are failures
        logger.warn('Email verification failed from error response', { 
          code: errorCode, 
          message: errorMessage.substring(0, 100) 
        });
        return {
          success: false,
          message: errorMessage || 'Email verification failed',
          data: null
        };
      }
      
      // Handle refresh token deletion errors in exception handler (for errors without data)
      if (error.isRefreshTokenDeleteError || 
          error.message?.includes('prisma.refreshToken.delete')) {
        logger.info('✅ Email verified successfully (refresh token cleanup error handled)');
        return {
          success: true,
          message: 'Email verified successfully',
          data: null
        };
      }

      // For other errors without data (network errors, etc.), return failure
      logger.warn('Email verification failed - network or unknown error', { error: error.message });
      return {
        success: false,
        message: error.message || 'Email verification failed',
        data: null
      };
    }
  },

  /**
   * Verifies the logged-in user's email address using OTP sent to their email.
   * Used for level one verification in settings page when user is already authenticated.
   * According to Postman collection: POST /api/auth/verify-loggedin-email with { "otp": "..." }
   * @param {string} otp - The OTP code received via email
   * @returns {Promise<Object>} API response confirming email verification
   */
  async verifyLoggedInEmail(otp) {
    try {
      const data = await apiRequest('/auth/verify-loggedin-email', {
        method: 'POST',
        body: JSON.stringify({ otp: otp.trim() }),
      });

      return {
        success: data?.code === '00',
        message: data?.message || 'Email verified successfully',
        data: data?.data || null,
        code: data?.code
      };
    } catch (error) {
      // If the response has code '00', it's still a success even if there's an error in processing
      if (error?.response?.code === '00' || error?.code === '00') {
        return {
          success: true,
          message: error?.response?.message || error?.message || 'Email verified successfully',
          data: error?.response?.data || null,
          code: '00'
        };
      }
      throw error;
    }
  },

  /**
   * Logs out the user by invalidating the session on the server.
   * This ensures tokens are invalidated server-side for security.
   * Sends the access token to the server for invalidation.
   * @returns {Promise<Object>} Logout response from the API with accessToken field
   */
  async logout() {
    try {
      // Get the access token and refresh token to send with the logout request
      const accessToken = tokenService.getAccessToken();
      const refreshToken = tokenService.getRefreshToken();

      // Call logout endpoint to invalidate server-side session
      // The endpoint expects the access token in body and returns { accessToken: "" }
      // The Authorization header with Bearer token is automatically added by apiClient
      const data = await apiRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({
          accessToken: accessToken || '',
          refreshToken: refreshToken || '',
        }),
      });

      // The response should be { accessToken: "" } or similar structure
      logger.debug('Logout API response', { data });
      return data;
    } catch (error) {
      // Even if API call fails, we still want to clear client-side data
      logger.warn('Logout API call failed, but continuing with client-side cleanup', { error });
      logger.error('Logout error details', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      // Return success so client-side cleanup can proceed
      return { code: '00', message: 'Logout initiated', accessToken: '' };
    }
  },

};

export default authService;
